import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import {
  ISellerListingUploadVideo,
  UploadedVideoTypes,
} from "@/interfaces/listing/SellerListing";
import { usePostHog } from "posthog-js/react";
import { PostHogFlags } from "@/interfaces/PostHogFlags";
import { getEmbedYoutubeUrl } from "@/libs/listing-utils";
import CustomVideo, { CustomVideoConfig } from "../ui/custom-video";
import { AnimatePresence, motion } from "framer-motion";
import VideoListModal from "./VideoListModal";

interface VideoItemProps {
  video: VideoListItem;
  showViewAllVideosBtn: boolean;
  index: number;
}

const VideoItem: React.FC<VideoItemProps> = ({
  video,
  showViewAllVideosBtn,
  index,
}) => {
  const containerEl = useRef(null);
  const [videoConfig, setVideoConfig] = useState<CustomVideoConfig>();
  useEffect(() => {
    const updateVideoSize = () => {
      if (video.type === UploadedVideoTypes.Custom && containerEl.current) {
        const { clientWidth, clientHeight } = containerEl.current;
        const config: CustomVideoConfig = {
          ...video,
          type: UploadedVideoTypes.Custom,
          options: {
            width: clientWidth,
            height: clientHeight,
          },
        };

        setVideoConfig(config);
      }
    };

    updateVideoSize();

    window.addEventListener("resize", updateVideoSize);

    return () => {
      window.removeEventListener("resize", updateVideoSize);
    };
  }, [video]);

  return (
    <div
      ref={containerEl}
      className={`md:w-[75%] md:h-[395px] w-full h-[225px] mb-4 ${
        showViewAllVideosBtn ? "w-1/2" : ""
      } ${index > 0 ? "ml-0 md:ml-3" : ""}`}
    >
      {video.type === UploadedVideoTypes.Custom && videoConfig && (
        <CustomVideo video={videoConfig} />
      )}
      {video.type === UploadedVideoTypes.YouTube && (
        <iframe
          width="100%"
          height="100%"
          src={video.safeUrl}
          allowFullScreen
        ></iframe>
      )}
    </div>
  );
};

interface VideoListItemCustom extends ISellerListingUploadVideo {
  type: UploadedVideoTypes.Custom;
}

interface VideoListItemYoutube extends ISellerListingUploadVideo {
  type: UploadedVideoTypes.YouTube;
  safeUrl: string;
}

type VideoListItem = VideoListItemCustom | VideoListItemYoutube;

interface VideoListProps {
  videos: ISellerListingUploadVideo[];
}

const VideoList: React.FC<VideoListProps> = ({ videos }) => {
  const posthog = usePostHog();
  const [list, setList] = useState<VideoListItem[]>([]);
  const [displayedList, setDisplayedList] = useState<VideoListItem[]>([]);
  const [showViewAllVideosBtn, setShowViewAllVideosBtn] = useState(false);
  const [showVideoListModal, setShowVideoListModal] = useState(false);

  useEffect(() => {
    if (videos.length > 0) {
      initDetails(videos);
    }
  }, [videos]);

  const initDetails = (videos: ISellerListingUploadVideo[]) => {
    const isUploadingCustomVideosEnabled = posthog.isFeatureEnabled(
      PostHogFlags.UploadCustomVideos
    );
    const filteredVideos = isUploadingCustomVideosEnabled
      ? videos
      : videos.filter((video) => video.type !== UploadedVideoTypes.Custom);

    const allList = getAllList(filteredVideos);
    setList(allList);
    setDisplayedList(getDisplayedList(allList));
    setShowViewAllVideosBtn(allList.length > 2);
  };

  const getAllList = (list: ISellerListingUploadVideo[]): VideoListItem[] => {
    return list.map((video) => {
      const safeUrl = getEmbedYoutubeUrl(DOMPurify.sanitize(video.url));
      const type = video.type as any;

      return {
        ...video,
        ...(video.type === UploadedVideoTypes.YouTube && { safeUrl }),
        type,
      };
    });
  };

  const getDisplayedList = (list: VideoListItem[]): VideoListItem[] => {
    return list.slice(0, 2);
  };

  const onViewAllVideosClick = () => {
    setShowVideoListModal(true);
  };

  return (
    <>
      <div className="video-section p-3">
        <div className="text-[#212529] text-[22px] font-bold leading-[20px]">
          Video
        </div>
        <div className="mt-3 flex flex-col md:flex-row">
          {displayedList.map((video, i) => (
            <VideoItem
              key={i}
              video={video}
              showViewAllVideosBtn={showViewAllVideosBtn}
              index={i}
            />
          ))}
        </div>
        {showViewAllVideosBtn && (
          <button
            className="btn-gray btn-view-all mt-0 mt-md-4 w-[235px] py-3 px-4"
            onClick={onViewAllVideosClick}
          >
            View all videos
          </button>
        )}
      </div>

      {showVideoListModal && (
        <AnimatePresence>
          {showVideoListModal && (
            <motion.div
              initial={{ y: -100 }}
              animate={{ y: 0 }}
              exit={{ y: 0, transition: { duration: 0 } }}
              transition={{ duration: 0.3 }}
              className="fixed top-0 left-0 w-full h-screen z-60 inset-0"
            >
              <VideoListModal
                isOpen={showVideoListModal}
                setIsOpen={setShowVideoListModal}
                videos={list}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {showVideoListModal && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black opacity-50"></div>
        </div>
      )}
    </>
  );
};

VideoList.propTypes = {
  videos: PropTypes.array.isRequired,
};

export default VideoList;
